/*
 * RZ Draggable Marquee with Lightbox Zoom
 * Combines: GSAP marquee (Observer + ScrollTrigger) + Lightbox (Flip)
 */

function initDraggableMarqueeZoom() {
  const sections = document.querySelectorAll("[data-draggable-marquee-zoom]");

  sections.forEach((section) => {
    if (section.getAttribute("data-draggable-marquee-zoom") === "initialized") return;

    const wrapper = section.querySelector("[data-draggable-marquee-init]");
    if (!wrapper) return;

    // ————————— MARQUEE SETUP ————————— //
    const collection = wrapper.querySelector("[data-draggable-marquee-collection]");
    const list = wrapper.querySelector("[data-draggable-marquee-list]");
    if (!collection || !list) return;

    const getNumberAttr = (el, name, fallback) => {
      const value = parseFloat(el.getAttribute(name));
      return Number.isFinite(value) ? value : fallback;
    };

    const duration = getNumberAttr(wrapper, "data-duration", 20);
    const multiplier = getNumberAttr(wrapper, "data-multiplier", 40);
    const sensitivity = getNumberAttr(wrapper, "data-sensitivity", 0.01);
    const wrapperWidth = wrapper.getBoundingClientRect().width;
    const listWidth = list.scrollWidth || list.getBoundingClientRect().width;
    if (!wrapperWidth || !listWidth) return;

    const minRequiredWidth = wrapperWidth + listWidth + 2;
    while (collection.scrollWidth < minRequiredWidth) {
      const listClone = list.cloneNode(true);
      listClone.setAttribute("data-draggable-marquee-clone", "");
      listClone.setAttribute("aria-hidden", "true");
      collection.appendChild(listClone);
    }

    const wrapX = gsap.utils.wrap(-listWidth, 0);
    gsap.set(collection, { x: 0 });

    const marqueeLoop = gsap.to(collection, {
      x: -listWidth,
      duration,
      ease: "none",
      repeat: -1,
      onReverseComplete: () => marqueeLoop.progress(1),
      modifiers: {
        x: (x) => wrapX(parseFloat(x)) + "px"
      },
    });

    const initialDirectionAttr = (wrapper.getAttribute("data-direction") || "left").toLowerCase();
    const baseDirection = initialDirectionAttr === "right" ? -1 : 1;
    const timeScale = { value: baseDirection };

    wrapper.setAttribute("data-direction", baseDirection < 0 ? "right" : "left");
    if (baseDirection < 0) marqueeLoop.progress(1);

    function applyTimeScale() {
      marqueeLoop.timeScale(timeScale.value);
      wrapper.setAttribute("data-direction", timeScale.value < 0 ? "right" : "left");
    }
    applyTimeScale();

    const marqueeObserver = Observer.create({
      target: wrapper,
      type: "pointer,touch",
      preventDefault: true,
      debounce: false,
      onChangeX: (observerEvent) => {
        // Don't respond to drag when lightbox is open
        if (section.querySelector('.rz-lightbox-wrap.is-active')) return;

        let velocityTimeScale = observerEvent.velocityX * -sensitivity;
        velocityTimeScale = gsap.utils.clamp(-multiplier, multiplier, velocityTimeScale);
        gsap.killTweensOf(timeScale);
        const restingDirection = velocityTimeScale < 0 ? -1 : 1;
        gsap.timeline({ onUpdate: applyTimeScale })
          .to(timeScale, { value: velocityTimeScale, duration: 0.1, overwrite: true })
          .to(timeScale, { value: restingDirection, duration: 1.0 });
      }
    });

    ScrollTrigger.create({
      trigger: wrapper,
      start: "top bottom",
      end: "bottom top",
      onEnter: () => { marqueeLoop.resume(); applyTimeScale(); marqueeObserver.enable(); },
      onEnterBack: () => { marqueeLoop.resume(); applyTimeScale(); marqueeObserver.enable(); },
      onLeave: () => { marqueeLoop.pause(); marqueeObserver.disable(); },
      onLeaveBack: () => { marqueeLoop.pause(); marqueeObserver.disable(); }
    });

    wrapper.setAttribute("data-draggable-marquee-init", "initialized");

    // ————————— LIGHTBOX SETUP ————————— //
    const lightboxEnabled = section.getAttribute("data-lightbox-enabled") === "true";
    if (!lightboxEnabled) {
      section.setAttribute("data-draggable-marquee-zoom", "initialized");
      return;
    }

    const lightboxWrapper = section.querySelector('[data-lightbox="wrapper"]');
    if (!lightboxWrapper) {
      section.setAttribute("data-draggable-marquee-zoom", "initialized");
      return;
    }

    const lightboxItems = section.querySelectorAll('[data-lightbox="item"]');
    const lightboxNav = section.querySelectorAll('[data-lightbox="nav"]');
    const counterCurrent = section.querySelector('[data-lightbox="counter-current"]');
    const counterTotal = section.querySelector('[data-lightbox="counter-total"]');
    const btnPrev = section.querySelector('[data-lightbox="prev"]');
    const btnNext = section.querySelector('[data-lightbox="next"]');
    const btnClose = section.querySelector('[data-lightbox="close"]');

    if (counterTotal) {
      const totalImages = list.querySelectorAll('[data-lightbox="trigger"]').length;
      counterTotal.textContent = totalImages;
    }

    const mainTimeline = gsap.timeline();
    let savedTimeScale = 1;

    // ————————— PAUSE / RESUME MARQUEE ————————— //
    function pauseMarquee() {
      savedTimeScale = timeScale.value;
      gsap.killTweensOf(timeScale);
      marqueeLoop.pause();
      marqueeObserver.disable();
    }

    function resumeMarquee() {
      timeScale.value = savedTimeScale;
      marqueeLoop.resume();
      applyTimeScale();
      marqueeObserver.enable();
    }

    // ————————— UPDATE ACTIVE ITEM ————————— //
    function updateActiveItem(index) {
      lightboxItems.forEach(item => item.classList.remove('is-active'));
      lightboxItems[index].classList.add('is-active');
      if (counterCurrent) counterCurrent.textContent = index + 1;
    }

    // ————————— CLOSE LIGHTBOX ————————— //
    function closeLightbox() {
      mainTimeline.clear();
      gsap.killTweensOf([lightboxWrapper, lightboxNav, lightboxItems]);

      const tl = gsap.timeline({
        defaults: { ease: "power2.inOut" },
        onComplete: () => {
          lightboxWrapper.classList.remove('is-active');
          document.body.style.overflow = '';
          lightboxItems.forEach(item => {
            item.classList.remove('is-active');
            const img = item.querySelector('img');
            if (img) img.style.display = '';
          });

          const originalImg = section.querySelector('[data-lightbox="original"]');
          if (originalImg) gsap.set(originalImg, { clearProps: "all" });

          const originalParent = section.querySelector('[data-lightbox="original-parent"]');
          if (originalParent) originalParent.style.removeProperty('height');

          // Remove outside click listener
          section.removeEventListener('click', handleOutsideClick);

          // Resume marquee after close
          resumeMarquee();
        }
      });

      const originalItem = section.querySelector('[data-lightbox="original"]');
      const originalParent = section.querySelector('[data-lightbox="original-parent"]');

      if (originalItem && originalParent) {
        gsap.set(originalItem, { clearProps: "all" });
        originalParent.appendChild(originalItem);
        // Restore original data-lightbox values instead of removing
        originalParent.setAttribute('data-lightbox', 'trigger');
        originalItem.removeAttribute('data-lightbox');
        originalParent.style.removeProperty('height');
      }

      const activeLightboxSlide = section.querySelector('[data-lightbox="item"].is-active');

      tl.to(lightboxNav, {
        autoAlpha: 0,
        y: "1rem",
        duration: 0.4,
        stagger: 0
      })
      .to(lightboxWrapper, {
        backgroundColor: "rgba(0,0,0,0)",
        duration: 0.4
      }, "<")
      .to(activeLightboxSlide, {
        autoAlpha: 0,
        duration: 0.4,
      }, "<")
      .set([lightboxItems, activeLightboxSlide], { clearProps: "all" });

      mainTimeline.add(tl);
    }

    // ————————— CLICK OUTSIDE ————————— //
    function handleOutsideClick(event) {
      if (event.detail === 0) return;
      const clickedElement = event.target;
      const isOutside = !clickedElement.closest('[data-lightbox="item"].is-active img, [data-lightbox="nav"], [data-lightbox="close"], [data-lightbox="trigger"]');
      if (isOutside) closeLightbox();
    }

    // ————————— TRIGGER CLICKS (originals + clones) ————————— //
    section.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox="trigger"]');
      if (!trigger) return;
      if (lightboxWrapper.classList.contains('is-active')) return;

      const item = trigger.closest('.draggable-marquee__item');
      if (!item) return;

      const index = parseInt(item.getAttribute('data-index'), 10);
      if (isNaN(index) || index < 0 || index >= lightboxItems.length) return;

      // Pause marquee first
      pauseMarquee();

      mainTimeline.clear();

      const img = trigger.querySelector("img");
      if (!img) return;

      const state = Flip.getState(img);

      const triggerRect = trigger.getBoundingClientRect();
      trigger.style.height = triggerRect.height + "px";

      trigger.setAttribute('data-lightbox', 'original-parent');
      img.setAttribute('data-lightbox', 'original');

      updateActiveItem(index);

      section.addEventListener('click', handleOutsideClick);

      const tl = gsap.timeline();
      lightboxWrapper.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      const targetItem = lightboxItems[index];
      const lightboxImage = targetItem.querySelector('img');
      if (lightboxImage) lightboxImage.style.display = 'none';

      if (!targetItem.contains(img)) {
        targetItem.appendChild(img);
        tl.add(
          Flip.from(state, {
            targets: img,
            absolute: true,
            duration: 0.6,
            ease: "power2.inOut"
          }), 0
        );
      }

      tl.to(lightboxWrapper, {
        backgroundColor: "rgba(0,0,0,0.85)",
        duration: 0.6
      }, 0)
      .fromTo(lightboxNav, {
        autoAlpha: 0,
        y: "1rem"
      }, {
        autoAlpha: 1,
        y: "0rem",
        duration: 0.6,
        stagger: { each: 0.05, from: "center" }
      }, 0.2);

      mainTimeline.add(tl);
    });

    // ————————— NAV BUTTONS ————————— //
    if (btnNext) {
      btnNext.addEventListener('click', () => {
        const currentIndex = Array.from(lightboxItems).findIndex(item => item.classList.contains('is-active'));
        const nextIndex = (currentIndex + 1) % lightboxItems.length;
        updateActiveItem(nextIndex);
      });
    }

    if (btnPrev) {
      btnPrev.addEventListener('click', () => {
        const currentIndex = Array.from(lightboxItems).findIndex(item => item.classList.contains('is-active'));
        const prevIndex = (currentIndex - 1 + lightboxItems.length) % lightboxItems.length;
        updateActiveItem(prevIndex);
      });
    }

    if (btnClose) {
      btnClose.addEventListener('click', closeLightbox);
    }

    // ————————— KEYBOARD NAV ————————— //
    document.addEventListener('keydown', (event) => {
      if (!lightboxWrapper.classList.contains('is-active')) return;
      switch (event.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowRight':
          btnNext?.click();
          break;
        case 'ArrowLeft':
          btnPrev?.click();
          break;
      }
    });

    section.setAttribute("data-draggable-marquee-zoom", "initialized");
  });
}

document.addEventListener("DOMContentLoaded", () => {
  initDraggableMarqueeZoom();
});
